import { FC, useMemo } from 'react'
import StandingsRowWithConfig from '@sport1/news-styleguide/StandingsRowWithConfig'
import { StandingsRowProps } from '@/components/Standings/Row'
import getBaseProps from '@/components/Standings/Row/utils/getBaseProps'
import getTotalStats from '@/components/Standings/utils/getTotalStats'
import getDifferenceAsString from '@/components/Standings/utils/getDifferenceAsString'
import sportIdentifiers from '@/utils/sportIdentifiers/SportIdentifiersConfig'
import { useMobile } from '@/hooks/useMobile'

const AmericanFootballStandingsRow: FC<
    Pick<StandingsRowProps, 'row' | 'contextIds' | 'competition'>
> = ({ row, contextIds, competition }) => {
    const { isMobile } = useMobile()
    const baseProps = useMemo(
        () =>
            getBaseProps({
                row,
                sportIdentifier: sportIdentifiers.american_football.name,
                isMobile,
                contextIds,
            }),
        [contextIds, isMobile, row]
    )
    if (!baseProps) {
        return null
    }

    const {
        forValue = 0,
        againstValue = 0,
        played = 0,
        won = 0,
        drawn = 0,
        lost = 0,
        winPercentage = '',
        homeWon = 0,
        homeDrawn = 0,
        homeLoss = 0,
        awayWon = 0,
        awayDrawn = 0,
        awayLoss = 0,
    } = row

    return competition?.id &&
        ['sr:tournament:31371', 'sr:tournament:34498'].includes(competition.id) ? (
        <StandingsRowWithConfig
            {...baseProps}
            columns={[
                {
                    value: `${played}`,
                    width: [30, 30, 48, 48],
                },
                {
                    value: getTotalStats('S-N-U', won, drawn, lost),
                    width: [48, 48, 56, 64],
                },
                {
                    value: winPercentage,
                    bold: true,
                    width: [48, 48, 56, 64],
                },
                {
                    value: `${forValue}:${againstValue}`,
                    width: [0, 0, 72, 72],
                    display: ['none', 'none', 'flex', 'flex'],
                },
                {
                    value: getDifferenceAsString(forValue, againstValue),
                    width: [0, 0, 56, 56],
                    display: ['none', 'none', 'flex', 'flex'],
                },
            ]}
        />
    ) : (
        <StandingsRowWithConfig
            {...baseProps}
            columns={[
                {
                    value: `${played}`,
                    width: [30, 30, 48, 48],
                },
                {
                    value: getTotalStats('S-N-U', won, drawn, lost),
                    width: [48, 48, 56, 64],
                },
                {
                    value: winPercentage,
                    bold: true,
                    width: [48, 48, 56, 64],
                },
                {
                    value: getTotalStats('S-N-U', homeWon, homeDrawn, homeLoss),
                    width: [0, 0, 56, 64],
                    display: ['none', 'none', 'flex', 'flex'],
                },
                {
                    value: getTotalStats('S-N-U', awayWon, awayDrawn, awayLoss),
                    width: [0, 0, 64, 64],
                    display: ['none', 'none', 'flex', 'flex'],
                },
                {
                    value: `${forValue}:${againstValue}`,
                    width: [0, 0, 72, 72],
                    display: ['none', 'none', 'flex', 'flex'],
                },
                {
                    value: getDifferenceAsString(forValue, againstValue),
                    width: [0, 0, 56, 56],
                    display: ['none', 'none', 'flex', 'flex'],
                },
            ]}
        />
    )
}

export default AmericanFootballStandingsRow
